/* ============================================================
   Toee Tire — homepage Vehicle tab (Vehicle lookup).
   A dealer picks year → make → model → trim (or decodes a VIN) and gets
   the vehicle's Fitments, each with the in-stock tires of its size and the
   wheels that fit, per-warehouse stock, the same-day promise, Dealer price
   when signed in, and a Package quote that adds the whole set to the cart.

   Backend (dealer-pro App Proxy, /apps/pro):
     GET  /vehicle/options|vin|fitment  — dealerProVehicle.v1
          (dealer-pro docs/dealer-pro-vehicle-lookup-contract-v1.md)
     POST /quote-batch                  — dealerPro.v1, ≤20 ids, price null for guests
   Rules: docs/contexts/dealer-acquisition/CONTEXT.md (Fitment, Fitting wheel,
   Vehicle result, Package, Price masking). Plan: docs/superpowers/plans/
   2026-10-19-vehicle-lookup.md. Design: the "A + C ledger" artboard.

   Pure view-model functions are exported for tests
   (tests/toee-vehicle-lookup.test.js); the DOM controller below them is
   driven by assets/toee-home.js (mode switch + the shared Search button).
   ============================================================ */
(function (root, factory) {
  // displaySize / fitmentChip live in toee-vehicle-current.js (the header loads that, not this file).
  var current = typeof module === 'object' && module.exports ? require('./toee-vehicle-current.js') : root && root.ToeeVehicleCurrent;
  var api = factory(current);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.ToeeVehicleLookup = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (current) {
  'use strict';

  var displaySize = current.displaySize;
  var fitmentChip = current.fitmentChip;

  /* ── Pure view model ─────────────────────────────────────── */

  // quote-batch reports a warehouse above its display threshold as
  // `displayMode: "capped"` with no number ("more than 20").
  var CAP = 20;
  // Warehouses with their own column; the rest fold into "Other". Which ones and
  // in what order is data, not code: dealer-pro's deliveryWarehouse records
  // (isActive + showOnProductPage, sorted by displayOrder) — quote-batch sends them in that order.
  var MAX_WAREHOUSE_COLUMNS = 3;

  function shortWarehouse(label) {
    return String(label || '').replace(/\s+warehouse$/i, '').trim();
  }

  function money(n) {
    var v = Number(n);
    if (!isFinite(v)) return '';
    var parts = v.toFixed(2).split('.');
    return '$' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }

  /** "20+" for a capped figure: a floor, never an exact count. */
  function qtyText(r) {
    return r.capped ? r.qty + '+' : String(r.qty);
  }

  function warehouseRows(stock) {
    return stock && Array.isArray(stock.warehouses) ? stock.warehouses : [];
  }

  /** "Oakville" → "Oak", "North York" → "NY": a header that fits a number column. */
  function abbrWarehouse(label) {
    var words = String(label || '').trim().split(/\s+/).filter(Boolean);
    if (words.length > 1) return words.map(function (w) { return w.charAt(0).toUpperCase(); }).join('');
    var w = words[0] || '';
    return w.charAt(0).toUpperCase() + w.slice(1, 3).toLowerCase();
  }

  /**
   * One order from several partial lists. Each quote lists its warehouses in the
   * backend's displayOrder but leaves out the ones at zero (hideWhenZero). A new key
   * goes just before the first key it is known to precede, else at the end.
   */
  function mergeOrder(lists) {
    var out = [];
    lists.forEach(function (list) {
      list.forEach(function (key, i) {
        if (out.indexOf(key) >= 0) return;
        var before = -1;
        for (var j = i + 1; j < list.length && before < 0; j++) before = out.indexOf(list[j]);
        if (before < 0) out.push(key);
        else out.splice(before, 0, key);
      });
    });
    return out;
  }

  function warehouseKey(w) {
    return w.warehouseKey || String(w.warehouseLabel || '').toLowerCase();
  }

  /** Per-warehouse sums across quotes, in the backend's warehouse order. */
  function sumStock(quotes) {
    var byKey = {};
    var order = mergeOrder((quotes || []).map(function (q) { return warehouseRows(q && q.stock).map(warehouseKey); }));
    (quotes || []).forEach(function (q) {
      warehouseRows(q && q.stock).forEach(function (w) {
        var key = warehouseKey(w);
        if (!byKey[key]) {
          byKey[key] = { key: key, label: shortWarehouse(w.warehouseLabel), qty: 0, capped: false };
        }
        if (w.displayMode === 'capped') {
          byKey[key].qty += CAP;
          byKey[key].capped = true;
        } else if (typeof w.quantity === 'number' && w.quantity > 0) {
          byKey[key].qty += w.quantity;
        }
      });
    });
    return { rows: order.map(function (k) { return byKey[k]; }) };
  }

  /** The columns for one result: the first three warehouses, and the rest as "Other". */
  function warehouseColumns(quotes, max) {
    var n = max || MAX_WAREHOUSE_COLUMNS;
    var rows = sumStock(quotes).rows.map(function (r) { return { key: r.key, label: r.label, abbr: abbrWarehouse(r.label) }; });
    return { columns: rows.slice(0, n), other: rows.length > n ? rows.slice(n) : [] };
  }

  /** One row's figures laid out on those columns; "Other" sums the rest and keeps their names. */
  function stockCells(stock, layout) {
    var byKey = {};
    ((stock && stock.rows) || []).forEach(function (r) { byKey[r.key] = r; });
    var zero = { qty: 0, capped: false };
    var other = { qty: 0, capped: false, breakdown: [] };
    layout.other.forEach(function (c) {
      var r = byKey[c.key];
      if (!r || (!r.qty && !r.capped)) return;
      other.qty += r.qty;
      other.capped = other.capped || r.capped;
      other.breakdown.push(c.label + ' ' + qtyText(r));
    });
    return {
      columns: layout.columns.map(function (c) { return byKey[c.key] || zero; }),
      other: layout.other.length ? other : null,
    };
  }

  function indexQuotes(quotes) {
    var out = {};
    (quotes || []).forEach(function (q) { if (q && q.variantId != null) out[String(q.variantId)] = q; });
    return out;
  }

  function fieldError(q, field) {
    return (q && Array.isArray(q.errors) ? q.errors : []).some(function (e) { return e && e.field === field; });
  }

  /**
   * One row per in-stock SKU, in the backend's order. A SKU the catalogue lists
   * but no warehouse holds is left out (CONTEXT.md: the out-of-stock are not
   * listed); a SKU whose stock lookup FAILED stays, marked unknown — never a zero.
   */
  function rowsFor(hits, quotesById, opts) {
    var o = opts || {};
    return (hits || []).reduce(function (out, h) {
      var q = quotesById[String(h.variantId)];
      if (!q) return out;
      if (o.winterOnly && !h.winter) return out;
      var stockKnown = !!(q.stock && !fieldError(q, 'stock'));
      if (stockKnown && !q.stock.anyAvailable) return out;
      out.push({
        id: String(h.variantId),
        title: h.title || q.title || '',
        winter: !!h.winter,
        spec: h.spec || null,
        fit: h.fit || null,
        quote: q,
        stockKnown: stockKnown,
        stock: stockKnown ? sumStock([q]) : null,
        unitText: q.price && q.price.unit != null ? money(q.price.unit) : null,
      });
      return out;
    }, []);
  }

  /** Take `need` pieces warehouse by warehouse; a capped row has at least CAP + 1. */
  function allocate(rows, need) {
    var left = need;
    var parts = [];
    (rows || []).forEach(function (r) {
      if (left <= 0) return;
      var have = r.capped ? CAP + 1 : r.qty;
      if (have <= 0) return;
      var take = Math.min(left, have);
      parts.push({ label: r.label, take: take });
      left -= take;
    });
    return { parts: parts, complete: left <= 0, short: Math.max(left, 0) };
  }

  /**
   * The Package ledger. Amounts are quote-batch's own `lineSubtotal` at this
   * quantity (never recomputed here). Same-day only when EVERY line's promise is
   * same_day; for a guest the promise is the static line, so `sameDay` is null.
   */
  function packageView(lines, qty) {
    var priced = lines.length > 0 && lines.every(function (l) { return l.quote && l.quote.price && l.quote.price.lineSubtotal != null; });
    var total = 0;
    var fees = 0;
    var complete = true;
    var windows = [];
    var out = lines.map(function (l) {
      var q = l.quote;
      var a = allocate(sumStock([q]).rows, qty);
      complete = complete && a.complete;
      if (q.promise && q.promise.promiseWindow) windows.push(q.promise.promiseWindow);
      var sub = priced ? Number(q.price.lineSubtotal) : 0;
      // The tire recycling fee is the "Tire Recycle Fee" product's live price, priced by
      // Shopify inside quote-batch — never a rate written here. A Package is always 2+
      // pieces, so the single-piece delivery charge never applies to it.
      var fee = priced ? Number(q.price.recyclingFee) || 0 : 0;
      total += sub + fee;
      fees += fee;
      return {
        kind: l.kind,
        side: l.side || 'front',
        title: q.title || '',
        allocText: a.parts.map(function (p) { return p.label + ' ' + p.take; }).join(' · '),
        complete: a.complete,
        short: a.short,
        amountText: priced ? money(sub) : null,
        eachText: priced ? qty + ' × ' + money(q.price.unit) : null,
      };
    });
    return {
      lines: out,
      priced: priced,
      totalText: priced ? money(total) : null,
      feeText: priced && fees > 0 ? money(fees) : null,
      complete: complete,
      sameDay: windows.length === lines.length && lines.length > 0 ? windows.every(function (w) { return w === 'same_day'; }) : null,
    };
  }

  /**
   * One list row's amount at the quantity it was quoted for: goods, the tire
   * recycling fee and, for a single piece, the delivery charge — all from quote-batch
   * (Shopify's draftOrderCalculate). Tax is left to checkout. Guests get null.
   */
  function lineView(q, qty) {
    var pr = q && q.price;
    if (!pr || pr.lineSubtotal == null) return null;
    var fee = Number(pr.recyclingFee) || 0;
    var surcharge = Number(pr.deliverySurcharge) || 0;
    return {
      qty: qty,
      eachText: qty + ' × ' + money(pr.unit),
      feeText: fee > 0 ? money(fee) : null,
      surchargeText: surcharge > 0 ? money(surcharge) : null,
      totalText: money(Number(pr.lineSubtotal) + fee + surcharge),
    };
  }

  /** 72.0 → "72", 63.4 → "63.4": the hub ring catalogue's own spelling of a size. */
  function ringSize(mm) {
    var n = Number(mm);
    return isFinite(n) ? String(n) : String(mm || '');
  }

  /**
   * Hub rings for a wheel that needs one (fit "hub_ring"): the ring's outer diameter is
   * the wheel's centre bore, its inner diameter the vehicle hub — both known, so the
   * dealer only picks a material. From paymentstatussync's hub-ring catalogue
   * (hubRingFitment.v1); in the catalogue's material order. Empty when it has no such ring.
   */
  function ringOptions(catalog, od, id) {
    if (!catalog || !Array.isArray(catalog.skus)) return [];
    // Vehicle data and the catalogue can spell one hub 0.1 mm apart (95.2 vs 95.1), and
    // 0.1 mm is below what a printed ring resolves (see dealer-pro's direct-fit rule):
    // take the closest catalogue size within 0.1 mm on each side, in whole hundredths.
    var gap = function (a, b) { return Math.abs(Math.round((Number(a) - Number(b)) * 100)); };
    var best = null;
    catalog.skus.forEach(function (s) {
      if (!s) return;
      var d = gap(s.od, od) + gap(s.id, id);
      if (gap(s.od, od) <= 10 && gap(s.id, id) <= 10 && (!best || d < best.d)) best = { d: d, od: ringSize(s.od), id: ringSize(s.id) };
    });
    if (!best) return [];
    var order = Array.isArray(catalog.materials) ? catalog.materials : [];
    return catalog.skus
      .filter(function (s) { return s && ringSize(s.od) === best.od && ringSize(s.id) === best.id; })
      .map(function (s) { return { material: s.material, variantId: String(s.variantId), available: s.available !== false }; })
      .sort(function (a, b) { return order.indexOf(a.material) - order.indexOf(b.material); });
  }

  /** "Car Wheel Hub Centric Rings 4pcs": one unit is a set of four, so one set per four wheels. */
  function ringSets(wheelQty) {
    return Math.max(1, Math.ceil((Number(wheelQty) || 0) / 4));
  }

  /**
   * One material for every ring an add needs (needs: [{wheels, options}], one per
   * ring size): the cart lines, their dealer total from quote-batch, and whether all
   * are in stock. null when some ring size does not come in that material.
   */
  function ringPick(needs, quotes, material) {
    var items = [];
    var total = 0;
    var available = true;
    for (var i = 0; i < needs.length; i++) {
      var o = needs[i].options.filter(function (x) { return x.material === material; })[0];
      if (!o) return null;
      var sets = ringSets(needs[i].wheels);
      var pr = quotes[o.variantId] && quotes[o.variantId].price;
      if (!o.available) available = false;
      items.push({ id: Number(o.variantId), quantity: sets });
      total = total == null || !pr || pr.lineSubtotal == null ? null : total + Number(pr.lineSubtotal);
    }
    return { items: items, totalText: total == null ? null : money(total), available: available };
  }

  /** Shopify sends /variants/<id> to the product page with that variant selected. */
  function productUrl(variantId) {
    return '/variants/' + encodeURIComponent(String(variantId));
  }

  function cartItems(lines, qty) {
    return lines.map(function (l) { return { id: Number(l.quote.variantId), quantity: qty }; });
  }

  /** One quote-batch call per Fitment: tires first, de-duplicated, ≤20 (the backend caps too). */
  function fitmentIds(f) {
    var seen = {};
    var out = [];
    [f.tires.front, f.tires.rear, f.wheels.front, f.wheels.rear].forEach(function (list) {
      (list || []).forEach(function (h) {
        var id = String(h.variantId);
        if (!seen[id] && out.length < 20) {
          seen[id] = true;
          out.push(id);
        }
      });
    });
    return out;
  }

  /* ── DOM controller ──────────────────────────────────────── */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /** A section header's count tag ("3 in stock"): the number bold, the rest escaped. */
  function capHtml(text, count) {
    return esc(text).replace(String(count), '<b>' + count + '</b>');
  }
  function fill(tpl, vars) {
    return String(tpl || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) { return vars && vars[k] != null ? vars[k] : ''; });
  }

  function getJson(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        return { ok: res.ok, status: res.status, body: body || {} };
      });
    });
  }
  function postJson(url, payload, signal) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      signal: signal,
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        return { ok: res.ok, status: res.status, body: body || {} };
      });
    });
  }

  var LEVELS = ['year', 'make', 'model', 'trim'];

  function Controller(panel) {
    this.panel = panel;
    this.holder = panel.closest('[data-toee-search]');
    this.wrap = this.holder && this.holder.querySelector('[data-mode-fields="' + panel.getAttribute('data-mode-panel') + '"]');
    this.base = (panel.getAttribute('data-proxy-base') || '/apps/pro').replace(/\/$/, '');
    this.signedIn = panel.getAttribute('data-signed-in') === 'true';
    try {
      this.t = JSON.parse((panel.querySelector('[data-vl-i18n]') || {}).textContent || '{}');
    } catch (e) {
      this.t = {};
    }
    this.selects = {};
    var self = this;
    LEVELS.forEach(function (l) {
      self.selects[l] = self.wrap && self.wrap.querySelector('[data-vl-level="' + l + '"]');
    });
    this.msg = panel.querySelector('[data-vl-msg]');
    this.out = panel.querySelector('[data-vl-result]');
    this.searchBtn = this.holder && this.holder.querySelector('[data-toee-search-submit]');
    this.state = null;
    this.loadedYears = false;
    this.restoreGen = 0; // bumped by every dealer change; a stored-vehicle restore in flight stops when it moves
    // A Fitment's quote-batch answer, kept briefly so switching back is instant.
    // ponytail: 60 s in-memory per page; stock can move, so never longer.
    this.cache = {};
    this.buildFold();
    this.bind();
  }

  var QUOTE_CACHE_MS = 60000;
  // Winter tires carry a snowflake beside the title; its name ("Winter") is the label.
  var SNOWFLAKE = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M8 1v14M1.94 4.5l12.12 7M1.94 11.5l12.12-7"/>' +
    '<path d="M9.7 1.8 8 3.6 6.3 1.8M3.48 3.43 4.19 5.8 1.78 6.37M1.78 9.63 4.19 10.2 3.48 12.57M6.3 14.2 8 12.4 9.7 14.2M12.52 12.57 11.81 10.2 14.22 9.63M14.22 6.37 11.81 5.8 12.52 3.43"/></svg>';
  // Direct fit: a shield with a tick. Its words ("Direct fit") sit beside it, so the icon is decoration.
  var DIRECT_ICON = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M12 2.5 19.5 5.5v6c0 4.6-3.1 8.6-7.5 10-4.4-1.4-7.5-5.4-7.5-10v-6z"/><path d="M8.5 12l2.5 2.5 4.5-5"/></svg>';
  var CHEVRON = '<svg class="tt-vl-fold-chev" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M2.5 7.5 6 4l3.5 3.5"/></svg>';

  /**
   * The result can run long, so it sits in a drawer under a centred Hide / Show
   * button on its top rule. The drawer animates its row from 1fr to 0fr (no height
   * measuring); closed, it is inert so Tab skips what cannot be seen.
   */
  Controller.prototype.buildFold = function () {
    if (!this.out) return;
    var id = this.out.id || (this.out.id = 'tt-vl-result-' + Math.random().toString(36).slice(2, 8));
    this.drawer = document.createElement('div');
    this.drawer.className = 'tt-vl-drawer is-open';
    this.drawerInner = document.createElement('div');
    this.drawerInner.className = 'tt-vl-drawer-inner';
    this.out.parentNode.insertBefore(this.drawer, this.out);
    this.drawerInner.appendChild(this.out);
    this.drawer.appendChild(this.drawerInner);
    this.fold = document.createElement('div');
    this.fold.className = 'tt-vl-fold';
    this.fold.hidden = true;
    this.fold.innerHTML = '<button type="button" class="tt-vl-fold-btn" data-vl-action="fold" aria-expanded="true" aria-controls="' + id + '">' +
      '<span class="tt-vl-fold-text"></span><span class="tt-vl-fold-sum"></span>' + CHEVRON + '</button>';
    this.drawer.parentNode.insertBefore(this.fold, this.drawer);
    this.open = true;
  };

  /** The button's words, and — closed — what the hidden result is for, in its own case ("2.0i", not "2.0I"). */
  Controller.prototype.updateFoldLabel = function () {
    if (!this.fold) return;
    var t = this.t;
    var s = this.state;
    var f = s ? this.fitment() : null;
    var bits = [];
    if (!this.open && s && s.vehicle && s.vehicle.label) bits.push(s.vehicle.label);
    if (!this.open && f) bits.push(fitmentChip(f).sizes);
    this.fold.querySelector('.tt-vl-fold-text').textContent = this.open ? t.hide_results || '' : t.show_results || '';
    var sum = this.fold.querySelector('.tt-vl-fold-sum');
    sum.textContent = bits.length ? bits.join(' · ') : '';
    sum.hidden = !bits.length;
  };

  /** Open or close the drawer. `user` = the dealer pressed the button (may scroll back to it). */
  Controller.prototype.setFold = function (open, user) {
    if (!this.drawer) return;
    this.open = !!open;
    this.drawer.classList.toggle('is-open', this.open);
    this.fold.classList.toggle('is-closed', !this.open);
    this.drawerInner.inert = !this.open;
    var btn = this.fold.querySelector('button');
    btn.setAttribute('aria-expanded', this.open ? 'true' : 'false');
    this.updateFoldLabel();
    if (!this.open && this.out) {
      this.dockShown = false;
      var dock = this.out.querySelector('.tt-vl-dock.is-shown');
      if (dock) dock.classList.remove('is-shown');
    }
    // Closing from deep inside a long result: bring the button back into view so the
    // dealer is not left looking at the page below with nothing on screen.
    if (user && !this.open) {
      var top = this.fold.getBoundingClientRect().top;
      if (top < 90) {
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: window.pageYOffset + top - 120, behavior: reduce ? 'auto' : 'smooth' });
      }
    }
  };

  /**
   * While a request is in flight the result fades behind a progress bar and the
   * Search button is disabled with its label kept (DESIGN.md Form States). The
   * Fitment buttons stay live so the dealer can change their mind.
   */
  Controller.prototype.busy = function (on) {
    this.loading = !!on;
    if (this.out) {
      this.out.classList.toggle('is-loading', this.loading);
      this.out.setAttribute('aria-busy', this.loading ? 'true' : 'false');
    }
    if (this.searchBtn) this.searchBtn.disabled = this.loading;
  };

  /** Mark the chosen Fitment button at once, before its stock arrives. */
  Controller.prototype.markFitment = function (id) {
    if (!this.out) return;
    Array.prototype.forEach.call(this.out.querySelectorAll('.tt-vl-fit'), function (b) {
      var on = b.getAttribute('data-id') === id;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  Controller.prototype.say = function (key, vars, isError) {
    if (!this.msg) return;
    this.msg.textContent = key ? fill(this.t[key] || key, vars) : '';
    this.msg.classList.toggle('tt-vl-msg--error', !!isError);
    this.msg.hidden = !key;
  };

  Controller.prototype.options = function (q) {
    return getJson(this.base + '/vehicle/options' + (q ? '?' + q : ''));
  };

  Controller.prototype.fillSelect = function (level, options, keep) {
    var s = this.selects[level];
    if (!s) return;
    var placeholder = s.getAttribute('data-placeholder') || '';
    s.innerHTML = '<option value="">' + esc(placeholder) + '</option>' + (options || []).map(function (o) {
      return '<option value="' + esc(o.value) + '">' + esc(o.label) + '</option>';
    }).join('');
    s.disabled = !(options && options.length);
    if (keep) s.value = keep;
  };

  Controller.prototype.clearBelow = function (level) {
    var self = this;
    LEVELS.slice(LEVELS.indexOf(level) + 1).forEach(function (l) { self.fillSelect(l, []); });
  };

  Controller.prototype.query = function (uptoLevel) {
    var self = this;
    var parts = [];
    LEVELS.slice(0, LEVELS.indexOf(uptoLevel) + 1).forEach(function (l) {
      var v = self.selects[l] && self.selects[l].value;
      if (v) parts.push(l + '=' + encodeURIComponent(v));
    });
    return parts.join('&');
  };

  Controller.prototype.loadLevel = function (level, keep) {
    var self = this;
    var prev = LEVELS[LEVELS.indexOf(level) - 1];
    return this.options(prev ? this.query(prev) : '').then(function (r) {
      if (!r.ok) {
        self.say('error', null, true);
        return;
      }
      self.fillSelect(level, r.body.options || [], keep);
    }, function () { self.say('error', null, true); });
  };

  Controller.prototype.activate = function () {
    if (this.loadedYears) return;
    this.loadedYears = true;
    var self = this;
    var stored = !this.restored && current && current.store && current.store.read();
    this.restored = true; // a VIN pre-fill resets loadedYears; it must not be restored over
    if (!stored) return this.loadLevel('year');
    // Restore the current vehicle: year → make → model → trim, then its Fitment's result.
    // A level that fails or no longer offers the stored value stops the chain quietly.
    // A change by the dealer while this runs moves restoreGen and ends the chain: late answers must not refill the selects.
    var gen = this.restoreGen;
    var restore = function (i) {
      if (gen !== self.restoreGen) return;
      if (i === LEVELS.length) return self.submit(stored.fitmentId);
      var l = LEVELS[i];
      return self.loadLevel(l, stored[l]).then(function () {
        if (gen !== self.restoreGen) return;
        if (self.selects[l] && self.selects[l].value === stored[l]) return restore(i + 1);
      });
    };
    restore(0);
  };

  /** Save the shown vehicle + Fitment as the Current vehicle (the header chip follows). */
  Controller.prototype.remember = function () {
    var s = this.state;
    if (!(current && current.store && s && s.q)) return;
    current.store.save(current.fromFitmentResponse(s.q, { vehicle: s.vehicle, fitments: s.fitments }, s.fitmentId, new Date().toISOString()));
  };

  Controller.prototype.bind = function () {
    var self = this;
    LEVELS.forEach(function (l, i) {
      var s = self.selects[l];
      if (!s) return;
      s.addEventListener('change', function () {
        self.restoreGen++;
        self.clearBelow(l);
        self.say('');
        if (s.value && i < LEVELS.length - 1) self.loadLevel(LEVELS[i + 1]);
      });
    });
    var panel = this.panel;
    panel.addEventListener('click', function (e) {
      var el = e.target.closest('[data-vl-action]');
      if (!el || !panel.contains(el)) return;
      self.action(el.getAttribute('data-vl-action'), el);
    });
    var vin = panel.querySelector('[data-vl-vin-input]');
    if (vin) vin.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        self.decodeVin();
      }
    });
  };

  Controller.prototype.action = function (name, el) {
    var s = this.state;
    if (name === 'fold') return this.setFold(!this.open, true);
    if (name === 'vin-toggle') {
      var form = this.panel.querySelector('[data-vl-vin]');
      if (form) {
        form.hidden = !form.hidden;
        if (!form.hidden) {
          var input = form.querySelector('input');
          if (input) input.focus();
        }
      }
      return;
    }
    if (name === 'vin-decode') return this.decodeVin();
    if (!s) return;
    if (name === 'ring-mat') {
      s.ring.material = el.getAttribute('data-value');
      return this.render();
    }
    if (name === 'ring-add' || name === 'ring-skip') {
      var ring = s.ring;
      if (!ring) return;
      var pick = name === 'ring-add' && ringPick(ring.needs, ring.quotes, ring.material);
      return ring.done(pick ? pick.items : []);
    }
    s.ring = null; // any other choice changes what the offer was for
    if (name === 'fitment') {
      var id = el.getAttribute('data-id');
      // The chosen (or still-loading) size again: nothing to fetch, so no duplicate request.
      if (id === s.fitmentId) return;
      s.fitmentId = id;
      s.pick = {};
      s.qty = null;
      this.markFitment(id);
      this.remember();
      return this.loadFitment();
    }
    if (name === 'view') s.view = el.getAttribute('data-value');
    if (name === 'season') s.season = el.getAttribute('data-value');
    if (name === 'mode') s.mode = el.getAttribute('data-value');
    if (name === 'pick') s.pick[el.getAttribute('data-slot')] = el.getAttribute('data-id');
    if (name === 'qty') {
      s.qty = Math.max(1, Math.min(48, this.qty() + Number(el.getAttribute('data-step'))));
      this.requoteSoon();
    }
    if (name === 'add') return this.addToCart(el);
    if (name === 'row-add') return this.addRow(el.getAttribute('data-id'), el.getAttribute('data-side'));
    if (name === 'row-qty') {
      var rid = el.getAttribute('data-id');
      s.rowQty[rid] = Math.max(1, Math.min(48, this.rowQty(rid) + Number(el.getAttribute('data-step'))));
      if (s.rowQty[rid] !== s.quotedQty[rid]) this.requoteRow(rid);
    }
    this.render();
  };

  Controller.prototype.decodeVin = function () {
    var self = this;
    var input = this.panel.querySelector('[data-vl-vin-input]');
    var raw = String((input && input.value) || '').trim().toUpperCase();
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(raw)) {
      this.say('vin_invalid', null, true);
      return;
    }
    // An animation, not a sentence, while the VIN decodes (the bar is CSS on .is-loading).
    var form = this.panel.querySelector('[data-vl-vin]');
    var decodeBtn = this.panel.querySelector('[data-vl-action="vin-decode"]');
    var vinBusy = function (on) {
      if (form) {
        form.classList.toggle('is-loading', on);
        form.setAttribute('aria-busy', on ? 'true' : 'false');
      }
      if (decodeBtn) decodeBtn.disabled = on;
    };
    if (decodeBtn && decodeBtn.disabled) return;
    this.say('');
    vinBusy(true);
    getJson(this.base + '/vehicle/vin?vin=' + encodeURIComponent(raw)).then(function (r) {
      vinBusy(false);
      if (!r.ok) {
        self.say(r.body.error === 'vin_not_decoded' || r.body.error === 'invalid_vin' ? 'vin_not_found' : 'error', null, true);
        return;
      }
      var v = r.body.vehicle || {};
      self.restoreGen++; // a stored-vehicle restore still running must not refill over this VIN
      // Pre-fill only: the dealer still chooses the trim (CONTEXT.md, Vehicle lookup).
      self.fillSelect('year', [{ value: v.year, label: v.year }], v.year);
      self.loadedYears = false; // the full year list comes back on the next activation
      self.loadLevel('make', v.make)
        .then(function () { return v.make ? self.loadLevel('model', v.model || '') : null; })
        .then(function () {
          if (v.model) self.fillSelect('trim', r.body.trims || []);
          self.say(v.model ? 'vin_decoded' : 'vin_partial', { vehicle: v.label || '' });
          var next = self.selects[v.model ? 'trim' : 'model'];
          if (next) next.focus();
        });
    }, function () {
      vinBusy(false);
      self.say('error', null, true);
    });
  };

  /**
   * The first search's placeholder: the result's outline (vehicle line, size
   * buttons, stock cells, rows) shimmering under the progress bar, so the panel
   * answers at once with no sentence to read. Screen readers get the label.
   */
  Controller.prototype.skeleton = function () {
    var span = function (n, cls) { return new Array(n + 1).join('<span class="' + cls + '"></span>'); };
    return '<div class="tt-vl-skel" role="status" aria-label="' + esc(this.t.loading || '') + '">' +
      '<div class="tt-vl-progress"></div>' +
      '<span class="tt-vl-bone tt-vl-bone--title"></span>' +
      '<span class="tt-vl-bone tt-vl-bone--sub"></span>' +
      '<div class="tt-vl-skel-chips">' + span(3, 'tt-vl-bone tt-vl-bone--chip') + '</div>' +
      '<div class="tt-vl-skel-cells">' + span(4, 'tt-vl-bone tt-vl-bone--cell') + '</div>' +
      span(3, 'tt-vl-bone tt-vl-bone--row') +
      '</div>';
  };

  /** Called by assets/toee-home.js when the shared Search button is pressed on this tab. */
  Controller.prototype.submit = function (preferFitmentId) {
    var self = this;
    if (this.loading && !this.state) return; // a search is already on its way
    var missing = LEVELS.filter(function (l) { return !(self.selects[l] && self.selects[l].value); });
    if (missing.length) {
      this.say('choose_all', null, true);
      if (this.selects[missing[0]]) this.selects[missing[0]].focus();
      return;
    }
    this.say('');
    this.cancel();
    this.state = null;
    if (this.out) {
      this.out.innerHTML = this.skeleton();
      this.out.hidden = false;
    }
    // A new search always opens the drawer.
    if (this.fold) this.fold.hidden = false;
    this.setFold(true);
    this.busy(true);
    var clearSkeleton = function () {
      if (self.out) {
        self.out.innerHTML = '';
        self.out.hidden = true;
      }
      if (self.fold) self.fold.hidden = true;
    };
    // The picks are read now, not when the answer returns: changing Year mid-flight empties the
    // lower selects, and the Current vehicle must be the car this request asked about.
    var optText = function (l) {
      var el = self.selects[l];
      return el && el.selectedIndex >= 0 ? el.options[el.selectedIndex].text : '';
    };
    var q = {
      year: this.selects.year.value,
      make: this.selects.make.value,
      model: this.selects.model.value,
      trim: this.selects.trim.value,
      makeName: optText('make'),
      modelName: optText('model'),
    };
    getJson(this.base + '/vehicle/fitment?' + this.query('trim')).then(function (r) {
      if (!r.ok) {
        self.busy(false);
        clearSkeleton();
        self.say(r.body.error === 'no_fitment' ? 'no_fitment' : 'error', null, true);
        return;
      }
      self.state = {
        q: q,
        vehicle: r.body.vehicle || {},
        fitments: r.body.fitments || [],
        fitmentId: current.pickFitmentId(r.body.fitments, preferFitmentId),
        view: 'all',
        season: 'all',
        mode: 'list',
        pick: {},
        qty: null,
        quotes: {},
        quotedQty: {}, // variantId → the quantity its quote was priced at
        rowQty: {}, // variantId → the quantity chosen on its List row
        added: {}, // variantId → pieces added to the cart from this result
        adding: {},
      };
      self.remember();
      self.loadFitment();
    }, function () {
      self.busy(false);
      clearSkeleton();
      self.say('error', null, true);
    });
  };

  Controller.prototype.fitment = function () {
    var s = this.state;
    return s && s.fitments.filter(function (f) { return f.id === s.fitmentId; })[0];
  };

  /** Default package quantity: a set of 4, or 2 per axle on a staggered Fitment. */
  Controller.prototype.qty = function () {
    var f = this.fitment();
    return this.state.qty || (f && f.staggered ? 2 : 4);
  };

  Controller.prototype.quote = function (ids, qty, signal) {
    if (!ids.length) return Promise.resolve({ ok: true, body: { quotes: [] } });
    return postJson(this.base + '/quote-batch', { variantIds: ids, quantity: qty }, signal);
  };

  /** Drop the request still in flight: a newer choice has replaced it. */
  Controller.prototype.cancel = function () {
    if (this.inflight) this.inflight.abort();
    this.inflight = null;
  };

  Controller.prototype.loadFitment = function () {
    var self = this;
    var f = this.fitment();
    if (!f) return;
    var key = f.id + '@' + this.qty();
    var hit = this.cache[key];
    this.cancel();
    if (hit && Date.now() - hit.at < QUOTE_CACHE_MS) {
      this.state.quotes = hit.quotes;
      this.state.quotedQty = Object.assign({}, hit.quotedQty);
      this.state.stockDown = false;
      this.busy(false);
      this.say('');
      this.render();
      return;
    }
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var gen = (this.gen = (this.gen || 0) + 1);
    var qtyAsked = this.qty();
    this.inflight = ctrl;
    this.busy(true);
    this.quote(fitmentIds(f), qtyAsked, ctrl && ctrl.signal).then(function (r) {
      if (gen !== self.gen) return;
      self.inflight = null;
      self.state.quotes = indexQuotes(r.ok ? r.body.quotes : []);
      self.state.quotedQty = {};
      Object.keys(self.state.quotes).forEach(function (id) { self.state.quotedQty[id] = qtyAsked; });
      self.state.stockDown = !r.ok;
      if (r.ok) self.cache[key] = { at: Date.now(), quotes: Object.assign({}, self.state.quotes), quotedQty: Object.assign({}, self.state.quotedQty) };
      self.busy(false);
      self.say('');
      self.render();
    }, function (e) {
      if (gen !== self.gen || (e && e.name === 'AbortError')) return;
      self.inflight = null;
      self.state.quotes = {};
      self.state.stockDown = true;
      self.busy(false);
      self.say('');
      self.render();
    });
  };

  /** Package prices are quote-batch's at the package quantity: re-ask when it changes (signed in only). */
  Controller.prototype.requoteSoon = function () {
    var self = this;
    clearTimeout(this.requoteTimer);
    if (!this.signedIn) return;
    this.requoteTimer = setTimeout(function () {
      var lines = self.packageLines();
      var ids = lines.map(function (l) { return String(l.quote.variantId); });
      var qtyAsked = self.qty();
      var gen = (self.qgen = (self.qgen || 0) + 1);
      self.busy(true);
      self.quote(ids, qtyAsked).then(function (r) {
        if (gen !== self.qgen) return;
        self.busy(false);
        if (!r.ok) return;
        (r.body.quotes || []).forEach(function (q) {
          self.state.quotes[String(q.variantId)] = q;
          self.state.quotedQty[String(q.variantId)] = qtyAsked;
        });
        self.render();
      }, function () {
        if (gen === self.qgen) self.busy(false);
      });
    }, 300);
  };

  Controller.prototype.sides = function (f) {
    return f.staggered ? ['front', 'rear'] : ['front'];
  };

  Controller.prototype.rows = function (kind, side) {
    var s = this.state;
    var f = this.fitment();
    var hits = (f[kind === 'tire' ? 'tires' : 'wheels'][side]) || [];
    return rowsFor(hits, s.quotes, { winterOnly: kind === 'tire' && s.season === 'winter' });
  };

  /** Chosen lines for the ledger: per axle, a tire and/or a wheel, defaulting to the first row. */
  Controller.prototype.packageLines = function () {
    var s = this.state;
    var f = this.fitment();
    var self = this;
    var lines = [];
    this.sides(f).forEach(function (side) {
      ['tire', 'wheel'].forEach(function (kind) {
        if (kind === 'tire' && s.view === 'wheels') return;
        if (kind === 'wheel' && s.view === 'tires') return;
        var rows = self.rows(kind, side);
        if (!rows.length) return;
        var slot = kind + '-' + side;
        var row = rows.filter(function (r) { return r.id === s.pick[slot]; })[0] || rows[0];
        s.pick[slot] = row.id;
        lines.push({ kind: kind, side: side, quote: row.quote, row: row });
      });
    });
    return lines;
  };

  Controller.prototype.addToCart = function (btn) {
    var self = this;
    var lines = this.packageLines();
    if (!lines.length) return;
    var qty = this.qty();
    var wheels = lines.filter(function (l) { return l.kind === 'wheel'; }).map(function (l) { return { row: l.row, side: l.side, qty: qty }; });
    var needs = this.ringNeeds(wheels);
    var put = function (rings) {
      self.state.ring = null;
      self.render();
      // render() replaced them; the ledger and the phone bar each carry one.
      var btns = self.out.querySelectorAll('[data-vl-action="add"]');
      var setAll = function (v) { Array.prototype.forEach.call(btns, function (b) { b.disabled = v; }); };
      setAll(true); // the label stays (DESIGN.md Form States)
      postJson('/cart/add.js', { items: cartItems(lines, qty).concat(rings) }).then(function (r) {
        if (!r.ok) throw new Error('add');
        window.location.href = '/cart';
      }).catch(function () {
        setAll(false);
        self.say('add_error', null, true);
      });
    };
    if (needs.length) {
      this.openRing('package', needs, put);
      var offer = this.out.querySelector('.tt-vl-ledger .tt-vl-ring');
      if (offer && offer.scrollIntoView) offer.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    put([]);
  };

  /* ── Hub rings: offered before wheels whose bore is larger than the hub go in the cart ── */

  Controller.prototype.hubFor = function (side) {
    var f = this.fitment();
    return side === 'rear' && f.rearHubBoreMm != null ? f.rearHubBoreMm : f.hubBoreMm;
  };

  /** One need per ring size (wheel bore → vehicle hub), with how many wheels it serves. */
  Controller.prototype.ringNeeds = function (wheels) {
    var self = this;
    var bySize = {};
    var needs = [];
    wheels.forEach(function (w) {
      if (w.row.fit !== 'hub_ring') return;
      var od = (w.row.spec || {}).cbMm;
      var hub = self.hubFor(w.side);
      var key = ringSize(od) + '>' + ringSize(hub);
      if (!bySize[key]) needs.push((bySize[key] = { od: od, hub: hub, wheels: 0, options: [] }));
      bySize[key].wheels += w.qty;
    });
    return needs;
  };

  /** paymentstatussync's hub-ring catalogue, fetched once per page (null if unavailable). */
  Controller.prototype.ringCatalog = function () {
    var self = this;
    var pid = this.panel.getAttribute('data-hub-ring-product');
    if (!pid) return Promise.resolve(null);
    if (!this.ringCat) {
      this.ringCat = getJson('/apps/paymentstatussync/fitment/hub-rings?product_id=' + encodeURIComponent(pid)).then(function (r) {
        if (r.ok && r.body.success !== false && r.body.truncated !== true) return r.body;
        self.ringCat = null; // try again next time
        return null;
      }, function () {
        self.ringCat = null;
        return null;
      });
    }
    return this.ringCat;
  };

  /**
   * The offer: sizes preset, the dealer picks a material tile (priced by quote-batch at
   * the sets needed) or skips. done(ringItems) does the add — [] for wheels only.
   */
  Controller.prototype.openRing = function (where, needs, done) {
    var self = this;
    var s = this.state;
    var ring = (s.ring = { where: where, needs: needs, done: done, loading: true, materials: [], quotes: {}, material: null });
    this.render();
    this.ringCatalog().then(function (cat) {
      needs.forEach(function (n) { n.options = ringOptions(cat, n.od, n.hub); });
      ring.materials = needs[0].options.map(function (o) { return o.material; }).filter(function (m) { return ringPick(needs, {}, m); });
      return Promise.all(needs.map(function (n) {
        var ids = n.options.map(function (o) { return o.variantId; });
        return self.quote(ids, ringSets(n.wheels)).then(function (r) {
          (r.ok ? r.body.quotes || [] : []).forEach(function (q) { ring.quotes[String(q.variantId)] = q; });
        }, function () {});
      }));
    }).then(function () {
      if (s.ring !== ring) return; // closed or replaced meanwhile
      ring.loading = false;
      ring.material = ring.materials.filter(function (m) { return ringPick(needs, ring.quotes, m).available; })[0] || null;
      self.render();
    });
  };

  Controller.prototype.ringHtml = function () {
    var t = this.t;
    var ring = this.state.ring;
    var spec = ring.needs.map(function (n) { return fill(t.ring_spec, { od: n.od, id: n.hub, sets: ringSets(n.wheels) }); }).join(' · ');
    // Slides in once when it opens, not again on every re-render.
    var h = ['<div class="tt-vl-ring' + (ring.shown ? '' : ' is-new') + '" role="group" aria-label="' + esc(t.ring_title) + '">' +
      '<div class="tt-vl-ring-head"><span class="tt-vl-label tt-vl-ink">' + esc(t.ring_title) + '</span><span class="tt-vl-cap">' + esc(spec) + '</span></div>'];
    ring.shown = true;
    var none = !ring.loading && !ring.materials.length;
    if (ring.loading) {
      h.push('<div class="tt-vl-ring-mats" aria-busy="true"><span class="tt-vl-bone tt-vl-bone--mat"></span><span class="tt-vl-bone tt-vl-bone--mat"></span><span class="tt-vl-bone tt-vl-bone--mat"></span></div>');
    } else if (none) {
      h.push('<p class="tt-vl-note">' + esc(t.ring_none) + '</p>');
    } else {
      h.push('<div class="tt-vl-ring-mats" role="radiogroup" aria-label="' + esc(t.ring_material) + '">');
      ring.materials.forEach(function (m) {
        var p = ringPick(ring.needs, ring.quotes, m);
        var on = ring.material === m;
        h.push('<button type="button" class="tt-vl-mat' + (on ? ' is-on' : '') + '" role="radio" aria-checked="' + on + '" data-vl-action="ring-mat" data-value="' + esc(m) + '"' + (p.available ? '' : ' disabled') + '>' +
          '<b>' + esc(m) + '</b>' +
          (t['ring_mat_' + String(m).toLowerCase()] ? '<span class="tt-vl-mat-note">' + esc(t['ring_mat_' + String(m).toLowerCase()]) + '</span>' : '') +
          '<span class="tt-vl-cost">' + esc(p.totalText || '—') + '</span>' +
          (p.available ? '' : '<span class="tt-vl-cap">' + esc(t.ring_sold_out) + '</span>') + '</button>');
      });
      h.push('</div>');
    }
    h.push('<div class="tt-vl-ring-act">' + (none ? '' : '<button type="button" class="tt-btn tt-btn--secondary tt-vl-add" data-vl-action="ring-skip">' + esc(t.ring_skip) + '</button>') +
      '<button type="button" class="tt-btn tt-btn--primary tt-vl-add" data-vl-action="' + (none ? 'ring-skip' : 'ring-add') + '"' + (none || ring.material ? '' : ' disabled') + '>' + esc(none ? t.ring_wheels_only : t.ring_add) + '</button></div></div>');
    return h.join('');
  };

  /* ── Rendering (DESIGN.md: 0 radius, hairlines, one panel shadow) ── */

  Controller.prototype.render = function () {
    var s = this.state;
    var f = this.fitment();
    if (!s || !f || !this.out) return;
    var t = this.t;
    var showT = s.view !== 'wheels';
    var showW = s.view !== 'tires';
    var oeFront = s.fitments.filter(function (x) { return x.sizing === 'oe'; })[0] || f;
    // One warehouse layout for the whole result so every table lines up.
    this.layout = warehouseColumns(Object.keys(s.quotes).map(function (k) { return s.quotes[k]; }));
    var whCount = this.layout.columns.length + (this.layout.other.length ? 1 : 0);
    var h = [];

    // Vehicle line + market label.
    h.push('<div class="tt-vl-vehicle"><div class="tt-vl-vehicle-name">' + esc(s.vehicle.label || '') + '</div>');
    h.push('<div class="tt-vl-spec">' +
      esc(t.spec_bolt) + ' <b>' + esc(f.boltPattern || '—') + '</b> · ' +
      esc(t.spec_hub) + ' <b>' + esc(f.hubBoreMm != null ? f.hubBoreMm + ' mm' : '—') + '</b> · ' +
      esc(t.spec_oe_wheel) + ' <b>' + esc(oeFront.front.rimDiameterIn + '×' + oeFront.front.rimWidthIn + (oeFront.front.offsetMm != null ? ' ET' + oeFront.front.offsetMm : '')) + '</b></div>');
    if (s.vehicle.market === 'US') h.push('<div class="tt-vl-us">' + esc(t.us_data) + '</div>');
    h.push('</div>');

    // One button per Fitment: OE first, then upsize / downsize (labelled).
    h.push('<div class="tt-vl-fitments" role="group" aria-label="' + esc(t.fitments_label) + '">');
    s.fitments.forEach(function (x) {
      var c = fitmentChip(x);
      var on = x.id === f.id;
      h.push('<button type="button" class="tt-vl-fit' + (on ? ' is-on' : '') + '" aria-pressed="' + on + '" data-vl-action="fitment" data-id="' + esc(x.id) + '">' +
        '<span class="tt-vl-fit-size">' + esc(c.sizes) + '</span><span class="tt-vl-fit-meta">' + esc(c.inches) +
        (x.sizing === 'oe' ? ' · ' + esc(t.oe) : ' · ' + esc(x.sizing === 'upsize' ? t.upsize : t.downsize) + ' ' + esc(c.tag)) +
        (x.staggered ? ' · ' + esc(t.staggered) : '') + '</span></button>');
    });
    h.push('</div>');
    // Shown only while .tt-vl-result.is-loading (Controller.busy).
    h.push('<div class="tt-vl-progress" aria-hidden="true"></div>');

    // Toolbar: view tabs · season · list / package.
    var tab = function (val, label) {
      var on = s.view === val;
      return '<button type="button" class="' + (on ? 'is-on' : '') + '" aria-pressed="' + on + '" data-vl-action="view" data-value="' + val + '">' + esc(label) + '</button>';
    };
    var chip = function (action, val, label, cur) {
      var on = cur === val;
      return '<button type="button" class="tt-vl-chip' + (on ? ' is-on' : '') + '" aria-pressed="' + on + '" data-vl-action="' + action + '" data-value="' + val + '">' + esc(label) + '</button>';
    };
    h.push('<div class="tt-vl-toolbar"><div class="tt-vl-tabs">' + tab('all', t.view_all) + tab('tires', t.view_tires) + tab('wheels', t.view_wheels) + '</div>');
    h.push('<div class="tt-vl-toolbar-right">');
    if (showT) h.push('<div class="tt-vl-chips">' + chip('season', 'all', t.season_all, s.season) + chip('season', 'winter', t.season_winter, s.season) + '</div>');
    h.push('<div class="tt-seg tt-vl-mode">' +
      '<button type="button" class="' + (s.mode === 'list' ? 'active' : '') + '" aria-pressed="' + (s.mode === 'list') + '" data-vl-action="mode" data-value="list">' + esc(t.mode_list) + '</button>' +
      '<button type="button" class="' + (s.mode === 'package' ? 'active' : '') + '" aria-pressed="' + (s.mode === 'package') + '" data-vl-action="mode" data-value="package">' + esc(t.mode_package) + '</button></div>');
    h.push('</div></div>');

    if (s.stockDown) h.push('<p class="tt-vl-note tt-vl-note--warn">' + esc(t.stock_unavailable) + '</p>');

    if (s.mode === 'package') h.push(this.renderLedger());

    // Two columns (tires 7 : wheels 5), one when a view hides a side.
    // Rows with quantity and Add need the full width, so the two tables stack then.
    h.push('<div class="tt-vl-cols ' + (showT && showW && !this.canBuy() ? 'is-both' : 'is-one') + '" style="--vl-wh: ' + Math.max(whCount, 1) + '">');
    if (showT) h.push(this.renderTires(f));
    if (showW) h.push(this.renderWheels(f));
    h.push('</div>');

    if (s.mode === 'package') h.push(this.dockHtml);
    if (s.mode === 'list' && !this.signedIn) {
      h.push('<div class="tt-vl-actions"><a class="tt-btn tt-btn--primary tt-btn--md" href="' + esc(this.panel.getAttribute('data-signup-url')) + '">' + esc(t.open_account) + '</a></div>');
    }
    this.out.innerHTML = h.join('');
    this.out.hidden = false;
    if (s.mode !== 'package') this.dockShown = false;
    this.watchDock();
    if (this.fold) {
      this.fold.hidden = false;
      this.updateFoldLabel(); // the summary follows the chosen size
    }
  };

  /** "Other" folds the 4th warehouse onward; its title names each one with its count. */
  Controller.prototype.otherTitle = function (other) {
    return fill(this.t.other_title, { list: other && other.breakdown.length ? other.breakdown.join(' · ') : this.layout.other.map(function (c) { return c.label; }).join(' · ') });
  };

  Controller.prototype.whCells = function (stock) {
    var self = this;
    var cells = stockCells(stock, this.layout);
    var html = cells.columns.map(function (r, i) {
      return '<span class="tt-vl-num' + (r.qty || r.capped ? '' : ' is-zero') + '" title="' + esc(self.layout.columns[i].label) + '" data-wh="' + esc(self.layout.columns[i].abbr) + '">' + esc(qtyText(r)) + '</span>';
    }).join('');
    if (cells.other) {
      var o = cells.other;
      html += '<span class="tt-vl-num tt-vl-other' + (o.qty || o.capped ? '' : ' is-zero') + '" title="' + esc(this.otherTitle(o)) + '" aria-label="' + esc(this.otherTitle(o)) + '" data-wh="' + esc(this.t.other) + '">' + esc(qtyText(o)) + '</span>';
    }
    return html;
  };

  Controller.prototype.rowHtml = function (r, kind, side, extra) {
    var s = this.state;
    var t = this.t;
    var pick = s.mode === 'package';
    var slot = kind + '-' + side;
    var on = pick && s.pick[slot] === r.id;
    var radio = pick
      ? '<button type="button" class="tt-vl-radio' + (on ? ' is-on' : '') + '" role="radio" aria-checked="' + on + '" aria-label="' + esc(r.title) + '" data-vl-action="pick" data-slot="' + slot + '" data-id="' + esc(r.id) + '"></button>'
      : '';
    var stock = r.stockKnown ? this.whCells(r.stock) : '<span class="tt-vl-num tt-vl-call" style="grid-column: span var(--vl-wh)">' + esc(t.call_to_confirm) + '</span>';
    var price = r.unitText ? '<span class="tt-vl-cost">' + esc(r.unitText) + '</span>' : '<span class="tt-vl-cost is-locked">' + esc(t.sign_in_price) + '</span>';
    var buy = this.canBuy();
    var qty = this.rowQty(r.id);
    var stale = buy && s.quotedQty[r.id] !== qty; // quantity changed, its price is on the way
    var amount = '';
    var act = '';
    if (buy) {
      var lv = lineView(r.quote, s.quotedQty[r.id]);
      if (lv) {
        amount = '<span class="tt-vl-amount">' + esc(lv.eachText) +
          (lv.feeText ? ' · ' + esc(t.recycling_fee) + ' ' + esc(lv.feeText) : '') +
          (lv.surchargeText ? ' · ' + esc(t.single_piece) + ' ' + esc(lv.surchargeText) : '') +
          ' = <b>' + esc(lv.totalText) + '</b> <span class="tt-vl-cap">' + esc(t.before_tax) + '</span></span>';
      }
      if (s.added[r.id]) amount += '<a class="tt-vl-added" href="/cart">' + esc(fill(t.added, { qty: s.added[r.id] })) + '</a>';
      act = '<span class="tt-vl-act"><span class="tt-vl-step tt-vl-step--sm">' +
        '<button type="button" aria-label="' + esc(t.fewer) + '" data-vl-action="row-qty" data-step="-1" data-id="' + esc(r.id) + '">−</button>' +
        '<span>' + qty + '</span>' +
        '<button type="button" aria-label="' + esc(t.more) + '" data-vl-action="row-qty" data-step="1" data-id="' + esc(r.id) + '">+</button></span>' +
        '<button type="button" class="tt-btn tt-btn--primary tt-vl-add" data-vl-action="row-add" data-id="' + esc(r.id) + '" data-side="' + side + '"' + (stale || s.adding[r.id] ? ' disabled' : '') + '>' + esc(t.add) + '</button></span>';
    }
    // A wheel that fits the vehicle's hub as it is carries a tag the length of the row (CSS: beside the name on a wide table, a band across the top elsewhere).
    var direct = kind === 'wheel' && r.fit === 'direct';
    return '<div class="tt-vl-row' + (direct ? ' tt-vl-row--direct' : '') + (on ? ' is-sel' : '') + (pick ? ' has-pick' : '') + (buy ? ' has-act' : '') + (stale ? ' is-stale' : '') + '"><span>' + radio + '</span><span class="tt-vl-main">' +
      '<a class="tt-vl-title" href="' + esc(productUrl(r.id)) + '">' + (r.winter
        // The last word and the snowflake wrap together, never the icon alone.
        ? esc(r.title).replace(/(\S+)$/, '<span class="tt-vl-nowrap">$1<span class="tt-vl-snow" role="img" aria-label="' + esc(t.season_winter) + '" title="' + esc(t.season_winter) + '">' + SNOWFLAKE + '</span></span>')
        : esc(r.title)) + '</a>' + (extra || '') + amount +
      (direct ? '<span class="tt-vl-direct">' + DIRECT_ICON + '<span>' + esc(t.direct_fit) + '</span></span>' : '') + '</span>' + stock + price + act + '</div>' +
      (s.ring && s.ring.where === 'row:' + r.id + ':' + side ? this.ringHtml() : '');
  };

  /** Rows carry their own quantity and Add button in List mode, for a signed-in dealer. */
  Controller.prototype.canBuy = function () {
    return this.signedIn && this.state.mode === 'list';
  };

  Controller.prototype.rowQty = function (id) {
    return this.state.rowQty[id] || this.qty();
  };

  /** A row's price is quote-batch's at that row's quantity: re-ask after the stepper settles. */
  Controller.prototype.requoteRow = function (id) {
    var self = this;
    var s = this.state;
    this.rowTimers = this.rowTimers || {};
    clearTimeout(this.rowTimers[id]);
    this.rowTimers[id] = setTimeout(function () {
      var qty = self.rowQty(id);
      self.quote([id], qty).then(function (r) {
        var q = r.ok && (r.body.quotes || [])[0];
        if (!q || self.state !== s || self.rowQty(id) !== qty) return; // superseded
        s.quotes[id] = q;
        s.quotedQty[id] = qty;
        self.render();
      });
    }, 300);
  };

  Controller.prototype.addRow = function (id, side) {
    var self = this;
    var s = this.state;
    var qty = this.rowQty(id);
    var wheel = side && this.rows('wheel', side).filter(function (r) { return r.id === id; })[0];
    var needs = wheel ? this.ringNeeds([{ row: wheel, side: side, qty: qty }]) : [];
    if (needs.length) {
      return this.openRing('row:' + id + ':' + side, needs, function (rings) {
        s.ring = null;
        self.putRow(id, qty, rings);
      });
    }
    this.putRow(id, qty, []);
  };

  Controller.prototype.putRow = function (id, qty, rings) {
    var self = this;
    var s = this.state;
    s.adding[id] = true;
    this.render();
    postJson('/cart/add.js', { items: [{ id: Number(id), quantity: qty }].concat(rings) }).then(function (r) {
      if (!r.ok) throw new Error('add');
      // The fee apps add the Tire Recycle Fee (and any single-piece delivery fee) in the cart.
      s.added[id] = (s.added[id] || 0) + qty;
      return getJson('/cart.js').then(function (c) {
        if (c.ok && typeof c.body.item_count === 'number') {
          Array.prototype.forEach.call(document.querySelectorAll('.cartCountSelector'), function (el) {
            el.textContent = String(c.body.item_count);
            el.classList.remove('hidden');
          });
        }
      });
    }).catch(function () {
      self.say('add_error', null, true);
    }).then(function () {
      s.adding[id] = false;
      self.render();
    });
  };

  Controller.prototype.thead = function (first) {
    var t = this.t;
    var cols = this.layout.columns.map(function (c) {
      return '<span title="' + esc(c.label) + '"><abbr title="' + esc(c.label) + '">' + esc(c.abbr) + '</abbr></span>';
    }).join('');
    if (this.layout.other.length) cols += '<span title="' + esc(this.otherTitle(null)) + '">' + esc(t.other) + '</span>';
    var buy = this.canBuy();
    return '<div class="tt-vl-thead' + (buy ? ' has-act' : '') + '"><span></span><span>' + esc(first) + '</span>' + cols + '<span>' + esc(t.price) + '</span>' +
      (buy ? '<span>' + esc(t.qty) + '</span>' : '') + '</div>';
  };

  /**
   * The line above every table: the same-day promise. Tires and wheels both carry
   * it, so side by side their headers and first rows sit on the same line. (The
   * per-warehouse totals boxes that used to sit here are gone: dealers see stock
   * per row in the table.)
   */
  Controller.prototype.summaryHtml = function (rows) {
    var t = this.t;
    var promise = rows[0].quote.promise || {};
    var line = promise.promiseWindow === 'same_day' ? t.same_day : (promise.displayLine || t.same_day);
    return '<p class="tt-vl-promise"><span class="tt-vl-dot"></span>' + esc(line) + '</p>';
  };

  Controller.prototype.renderTires = function (f) {
    var self = this;
    var t = this.t;
    var h = ['<div class="tt-vl-col tt-vl-col--tires">'];
    this.sides(f).forEach(function (side) {
      var axle = f[side];
      var rows = self.rows('tire', side);
      var size = displaySize(axle.tireSize);
      h.push('<div class="tt-vl-colhead"><span class="tt-vl-label">' + esc(fill(t.tires_heading, { size: size })) +
        (f.staggered ? ' · ' + esc(side === 'front' ? t.front : t.rear) : '') + '</span><span class="tt-vl-cap">' + capHtml(fill(t.in_stock_count, { count: rows.length }), rows.length) + '</span></div>');
      if (!rows.length) {
        h.push('<p class="tt-vl-note">' + esc(fill(t.no_tires, { size: size })) + '</p>');
        return;
      }
      h.push(self.summaryHtml(rows));
      h.push(self.thead(t.sku));
      rows.forEach(function (r) { h.push(self.rowHtml(r, 'tire', side)); });
    });
    h.push('</div>');
    return h.join('');
  };

  Controller.prototype.renderWheels = function (f) {
    var self = this;
    var t = this.t;
    var h = ['<div class="tt-vl-col tt-vl-col--wheels">'];
    this.sides(f).forEach(function (side) {
      var axle = f[side];
      var hub = side === 'rear' ? (f.rearHubBoreMm != null ? f.rearHubBoreMm : f.hubBoreMm) : f.hubBoreMm;
      var rows = self.rows('wheel', side);
      h.push('<div class="tt-vl-colhead"><span class="tt-vl-label">' + esc(fill(t.wheels_heading, { diameter: axle.rimDiameterIn + '″', bolt: f.boltPattern || '' })) +
        (f.staggered ? ' · ' + esc(side === 'front' ? t.front : t.rear) : '') + '</span><span class="tt-vl-cap">' + capHtml(fill(t.fitting_count, { count: rows.length }), rows.length) + '</span></div>');
      if (f.hubBoreMm == null) {
        h.push('<p class="tt-vl-note">' + esc(t.bore_unknown) + '</p>');
        return;
      }
      if (!rows.length) {
        h.push('<p class="tt-vl-note">' + esc(t.no_wheels) + '</p>');
        return;
      }
      h.push(self.summaryHtml(rows));
      h.push(self.thead(t.wheel));
      rows.forEach(function (r) {
        var sp = r.spec || {};
        // A hub-ring fit stays a plain "Hub ring a → b" note; Direct fit is the good news and is the row's own tag (rowHtml).
        var fitHtml = r.fit === 'direct' ? '' : ' · ' + esc(fill(t.hub_ring, { from: sp.cbMm, to: hub }));
        var meta = '<span class="tt-vl-meta">ET' + esc(sp.etMm) + (axle.offsetMm != null ? ' · ' + esc(fill(t.oe_offset, { et: axle.offsetMm })) : '') + fitHtml + '</span>';
        h.push(self.rowHtml(r, 'wheel', side, meta));
      });
    });
    // How "fit" was decided, as a footnote under the table — not above it, where it
    // would push the wheel table out of line with the tire table beside it.
    if (f.hubBoreMm != null) h.push('<p class="tt-vl-cap tt-vl-wheel-note">' + esc(t.wheels_note) + '</p>');
    h.push('</div>');
    return h.join('');
  };

  Controller.prototype.renderLedger = function () {
    var t = this.t;
    var f = this.fitment();
    var lines = this.packageLines();
    var qty = this.qty();
    var self = this;
    // A line priced at another quantity (a List row's stepper) is re-asked at this one;
    // until it lands the amounts read "—" rather than a figure for the wrong quantity.
    var stale = this.signedIn && lines.some(function (l) { return self.state.quotedQty[String(l.quote.variantId)] !== qty; });
    if (stale) this.requoteSoon();
    var p = packageView(stale ? lines.map(function (l) { return { kind: l.kind, side: l.side, quote: Object.assign({}, l.quote, { price: null }), row: l.row }; }) : lines, qty);
    var h = ['<div class="tt-vl-ledger"><div class="tt-vl-lhead"><div><div class="tt-vl-label tt-vl-ink">' + esc(t.package_title) + '</div>' +
      '<div class="tt-vl-cap">' + esc(t.package_hint) + '</div></div>' +
      '<div class="tt-vl-qty"><span class="tt-vl-label">' + esc(f.staggered ? t.qty_per_axle : t.qty) + '</span>' +
      '<div class="tt-vl-step"><button type="button" aria-label="' + esc(t.fewer) + '" data-vl-action="qty" data-step="-1">−</button><span>' + qty + '</span>' +
      '<button type="button" aria-label="' + esc(t.more) + '" data-vl-action="qty" data-step="1">+</button></div></div></div>'];
    if (!lines.length) h.push('<p class="tt-vl-note tt-vl-pad">' + esc(t.package_empty) + '</p>');
    p.lines.forEach(function (l) {
      var kind = l.kind === 'tire' ? t.line_tires : t.line_wheels;
      if (f.staggered) kind += ' · ' + (l.side === 'front' ? t.front : t.rear);
      h.push('<div class="tt-vl-lrow"><span class="tt-vl-label">' + esc(kind) + '</span>' +
        '<span><span class="tt-vl-title">' + esc(l.title) + '</span><span class="tt-vl-meta"><span class="tt-vl-dot' + (l.complete ? '' : ' is-warn') + '"></span>' +
        esc(l.complete ? l.allocText : fill(t.short, { count: qty - l.short })) + '</span></span>' +
        '<span class="tt-vl-meta tt-vl-right">' + esc(l.eachText || qty + ' ×') + '</span>' +
        '<span class="tt-vl-cost tt-vl-right">' + esc(l.amountText || '—') + '</span></div>');
    });
    if (p.feeText) {
      h.push('<div class="tt-vl-lrow"><span class="tt-vl-label">' + esc(t.recycling_fee) + '</span><span class="tt-vl-meta">' + esc(t.recycling_fee_note) + '</span>' +
        '<span></span><span class="tt-vl-cost tt-vl-right">' + esc(p.feeText) + '</span></div>');
    }
    var hasRing = lines.some(function (l) { return l.kind === 'wheel' && l.row.fit === 'hub_ring'; });
    if (this.state.ring && this.state.ring.where === 'package') h.push('<div class="tt-vl-pad">' + this.ringHtml() + '</div>');
    else if (hasRing) h.push('<p class="tt-vl-cap tt-vl-pad">' + esc(t.hub_ring_cart) + '</p>');
    var avail = !lines.length ? '' : !p.complete ? t.not_enough : p.sameDay === false ? t.complete_next_day : t.complete_same_day;
    h.push('<div class="tt-vl-ltotal"><div class="tt-vl-cap tt-vl-body"><span class="tt-vl-dot' + (p.complete ? '' : ' is-warn') + '"></span>' + esc(avail) + '</div>');
    // The same total, pinned to the bottom of a phone screen while the tables are in view.
    var dock = '<div class="tt-vl-dock' + (this.dockShown ? ' is-shown' : '') + '">';
    if (this.signedIn) {
      var cta = this.state.view === 'tires' ? t.add_tires : this.state.view === 'wheels' ? t.add_wheels : t.add_set;
      dock += '<div><div class="tt-vl-label">' + esc(t.total_label) + '</div><div class="tt-vl-total">' + esc(p.totalText || '—') + '</div></div>' +
        '<button type="button" class="tt-btn tt-btn--primary tt-btn--md" data-vl-action="add"' + (lines.length ? '' : ' disabled') + '>' + esc(cta) + '</button>';
      h.push('<div class="tt-vl-total-wrap"><div class="tt-vl-right"><div class="tt-vl-label">' + esc(t.total_label) + '</div><div class="tt-vl-total">' + esc(p.totalText || '—') + '</div><div class="tt-vl-cap">' + esc(t.before_tax) + '</div></div>' +
        '<button type="button" class="tt-btn tt-btn--primary tt-btn--md" data-vl-action="add"' + (lines.length ? '' : ' disabled') + '>' + esc(cta) + '</button></div>');
    } else {
      dock += '<span class="tt-vl-guest">' + esc(t.sign_in_total) + '</span>' +
        '<a class="tt-btn tt-btn--primary tt-btn--md" href="' + esc(self.panel.getAttribute('data-login-url')) + '">' + esc(t.sign_in) + '</a>';
      h.push('<div class="tt-vl-total-wrap"><span class="tt-vl-guest">' + esc(t.sign_in_total) + '</span>' +
        '<a class="tt-btn tt-btn--secondary tt-btn--md" href="' + esc(self.panel.getAttribute('data-login-url')) + '">' + esc(t.sign_in) + '</a>' +
        '<a class="tt-btn tt-btn--primary tt-btn--md" href="' + esc(self.panel.getAttribute('data-signup-url')) + '">' + esc(t.open_account) + '</a></div>');
    }
    h.push('</div></div>');
    this.dockHtml = dock + '</div>';
    return h.join('');
  };

  /**
   * Show the phone total bar while the tables are on screen and the ledger's own total
   * is not (CSS keeps it to phones). A re-render keeps the bar's state: no slide each time.
   */
  Controller.prototype.watchDock = function () {
    var self = this;
    if (this.dockIO) this.dockIO.disconnect();
    var dock = this.out.querySelector('.tt-vl-dock');
    if (!dock || typeof IntersectionObserver === 'undefined') return;
    var cols = this.out.querySelector('.tt-vl-cols');
    var total = this.out.querySelector('.tt-vl-ltotal');
    var seen = { cols: false, total: false };
    // The margins skip the strips under the sticky header and under the bar itself:
    // a total tucked behind the header is not one the dealer can see.
    this.dockIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { seen[e.target === total ? 'total' : 'cols'] = e.isIntersecting; });
      self.dockShown = seen.cols && !seen.total && self.open !== false;
      dock.classList.toggle('is-shown', self.dockShown);
    }, { rootMargin: '-90px 0px -80px 0px' });
    if (cols) this.dockIO.observe(cols);
    if (total) this.dockIO.observe(total);
  };

  /* ── Entry points (assets/toee-home.js) ──────────────────── */

  function controllerFor(panel) {
    if (!panel) return null;
    if (!panel.__toeeVehicle) panel.__toeeVehicle = new Controller(panel);
    return panel.__toeeVehicle;
  }

  return {
    // pure
    shortWarehouse: shortWarehouse,
    abbrWarehouse: abbrWarehouse,
    warehouseColumns: warehouseColumns,
    stockCells: stockCells,
    capHtml: capHtml,
    money: money,
    qtyText: qtyText,
    sumStock: sumStock,
    indexQuotes: indexQuotes,
    rowsFor: rowsFor,
    allocate: allocate,
    packageView: packageView,
    cartItems: cartItems,
    lineView: lineView,
    productUrl: productUrl,
    ringOptions: ringOptions,
    ringSets: ringSets,
    ringPick: ringPick,
    displaySize: displaySize,
    fitmentChip: fitmentChip,
    fitmentIds: fitmentIds,
    // DOM
    activate: function (panel) { var c = controllerFor(panel); if (c) c.activate(); },
    submit: function (panel) { var c = controllerFor(panel); if (c) c.submit(); },
  };
});
